import React, { useContext } from "react";
import {
  useProfile,
  formatAmount,
  getCurrencySymbol,
  useTaxSummary,
  TAX_YEARS,
} from "@moonscape/shared";
import { DocumentChartBarIcon } from "@heroicons/react/24/outline";
import { PrivacyModeContext } from "@/contexts";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyWidget } from "@/components/ui/empty-widget";
import { DHead, Fig } from "@/components/ms";
import { FigLink, HeadLink } from "./chrome";

export const TaxSummaryWidget = ({ taxYear }: { taxYear?: number }) => {
  const { profile } = useProfile();
  const { privacyMode } = useContext(PrivacyModeContext);
  const currentTaxYear = taxYear || (new Date().getFullYear() - 1); // filing for previous year

  // Get display format for tax year (e.g., "2023/24")
  const taxYearDisplay = TAX_YEARS.find(y => y.value === currentTaxYear)?.taxYear || `${currentTaxYear}`;

  const { data: taxSummary, isLoading } = useTaxSummary({
    taxYear: currentTaxYear,
  });

  const baseCurrency = profile?.baseCurrency || "USD";
  const currencySymbol = getCurrencySymbol(baseCurrency);

  // Early return for loading state
  if (isLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Tax Summary" right={taxYearDisplay} />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </section>
    );
  }

  // Check if there's any tax data
  const hasData = taxSummary && (
    taxSummary.capitalGains ||
    taxSummary.income ||
    taxSummary.contractIncome
  );

  const total = taxSummary?.total || {
    totalGain: 0,
    totalLoss: 0,
    totalProceeds: 0,
    estimatedTaxDue: 0,
    disposals: 0,
    gain: 0,
  };

  const netResult = total.gain;
  const isNetGain = netResult > 0;
  const isNetLoss = netResult < 0;

  // Format currency
  const formatCurrency = (amount: number) => formatAmount(amount, currencySymbol, true);

  const blur = privacyMode ? "blur-md" : undefined;

  return (
    <section className="flex flex-col">
      <DHead
        label="Tax Summary"
        right={<HeadLink to={`/tax?year=${currentTaxYear}`}>{taxYearDisplay}</HeadLink>}
      />

      {!hasData ? (
        <EmptyWidget
          icon={<DocumentChartBarIcon className="h-8 w-8" />}
          title="No tax data yet"
          description="Connect your exchanges and wallets to generate tax reports"
          action={{ label: "Connect Exchange", to: "/integrations" }}
        />
      ) : (
        <div className="flex flex-col">
          <Fig
            label="Total Net Result"
            sub={`${total.disposals} taxable events`}
            value={
              <span className={blur}>
                {isNetLoss ? "− " : isNetGain ? "+ " : ""}
                {formatCurrency(Math.abs(netResult))}
              </span>
            }
            tone={isNetGain ? "green" : isNetLoss ? "neg" : "ink"}
            strong
          />

          <Fig
            label="Estimated tax due"
            value={<span className={blur}>{formatCurrency(total.estimatedTaxDue)}</span>}
            tone="tan"
          />

          {taxSummary.capitalGains && (
            <FigLink
              to={`/tax/events?year=${currentTaxYear}`}
              label="Capital gains"
              sub={`${taxSummary.capitalGains.disposals} disposals · tax ${formatCurrency(
                taxSummary.capitalGains.estimated_tax_due
              )}`}
              value={<span className={blur}>{formatCurrency(taxSummary.capitalGains.gain)}</span>}
              tone={
                taxSummary.capitalGains.gain > 0
                  ? "green"
                  : taxSummary.capitalGains.gain < 0
                  ? "neg"
                  : "mute"
              }
            />
          )}

          {taxSummary.income && (
            <FigLink
              to={`/tax/events?year=${currentTaxYear}`}
              label="Income"
              sub={`${taxSummary.income.disposals} events · tax ${formatCurrency(
                taxSummary.income.estimated_tax_due
              )}`}
              value={<span className={blur}>{formatCurrency(taxSummary.income.gain)}</span>}
              tone={
                taxSummary.income.gain > 0
                  ? "green"
                  : taxSummary.income.gain < 0
                  ? "neg"
                  : "mute"
              }
            />
          )}

          {taxSummary.contractIncome && (
            <FigLink
              to={`/tax/events?year=${currentTaxYear}`}
              label="Contract income"
              sub={`${taxSummary.contractIncome.disposals} events · tax ${formatCurrency(
                taxSummary.contractIncome.estimated_tax_due
              )}`}
              value={
                <span className={blur}>{formatCurrency(taxSummary.contractIncome.gain)}</span>
              }
              tone={
                taxSummary.contractIncome.gain > 0
                  ? "green"
                  : taxSummary.contractIncome.gain < 0
                  ? "neg"
                  : "mute"
              }
            />
          )}
        </div>
      )}
    </section>
  );
};
